import Dependencies from '../utilities/dependencies';
import DateFormat from '../utilities/date-format';
import CmsApi from "../utilities/cms-api";

export default class BookingSearchForm {

	/**
	 * CSS selectors that must exist for this module to run
	 * @see {@link https://docs.fastrackdev.com/front-end-development.html#javascript-development}
	 */
	static getHolderSelectors = () => ['.booking-search-form-wrapper'];

	/**
	 * Dependencies that this module requires
	 * @see {@link https://docs.fastrackdev.com/front-end-development.html#javascript-development}
	 */
	static getDependencies = () => [Dependencies.libraries.pikaday];

	/**
	 * Runtime injected CSS classes
	 * @see {@link https://docs.fastrackdev.com/front-end-development.html#javascript-development}
	 */
	static getClassList = () => Object.create({});

	/** @type {HTMLElement} */
	#holderElement;

	/** @type {HTMLElement} */
	#btnBackElement;

	/** @type {HTMLElement} */
	#btnNextElement;

	/** @type {HTMLElement} */
	#btnSubmitElement;

	/** @type {String} */
	#bookingType;

	/** @type {String} */
	#currentSelectionID;

	/** @type {Number} */
	#maxGuestNum= 10;

	/** @type {Object} */
	#validation = {};

	/** @type {HTMLElement} */
	#calendarElement;

	/** @type {Pikaday} */
	#picker;

	/** @type {Number} */
	#currentMonth;

	/** @type {Number} */
	#currentYear;

	// /** @type {HTMLElement} */
	// #stayLengthElement;

	/** @type {HTMLElement} */
	#monthPickerElement;

	/** @type {Date} */
	#checkIn;

	/** @type {Date} */
	#checkOut;

	/** @type {HTMLElement} */
	#bookingFormElement;

	/** @type {HTMLElement} */
	#guestsNum = 0;

	/** @type {number} */
	#destination = 0;

	/** @type {HTMLElement} */
	#btnBackMobile;

	/** @type {HTMLElement} */
	#btnNextMobile;

	/** @type {Array<String>} */
	#mobileNextBtnDisabled = [];

	/** @type {Array<Property>} */
	#properties = [];

	/** @type {Array<any>} */
	#restaurants = [];

	static alpineData;

	/**
	 * @param {HTMLElement} holderElement
	 */
	constructor(holderElement) {
		this.#holderElement = holderElement;
		this.#btnBackElement = this.#holderElement.querySelector('.btn-back');
		this.#btnNextElement = this.#holderElement.querySelector('.btn-next');
		this.#btnSubmitElement = this.#holderElement.querySelector('.btn-submit');
		this.#calendarElement = this.#holderElement.querySelector('.calendar');
		this.#bookingFormElement = this.#holderElement.querySelector('#booking-form');
		this.onload();
	}

	async onload() {
		this.#getCurrentSelectionID();
		this.#handleBackBtn();
		this.#handleNextBtn();
		this.#handleCloseBtn();
		this.#handleFormOptions();

		this.#properties = await CmsApi.fetchProperties();
		// this.#restaurants = await CmsApi.fetchRestaurants();
	}

	#getCurrentSelectionID() {
		this.#currentSelectionID = this.#holderElement.querySelector('.booking-search-form-selection-wrapper.active').getAttribute('id');
	}

	#handleBackBtn() {
		this.#btnBackElement.addEventListener('click', (event) => {
			this.#scrollToPreviousSelection();
			if (this.#currentSelectionID === 'selection-0') {
				this.#btnBackElement.style.display = 'none';
			} else if (this.#currentSelectionID === 'selection-1') {
				this.#displayNextBtn();
				this.#hideSubmitBtn();
			}
			this.#btnNextElement.innerHTML = this.#currentSelectionID === 'selection-0' ? 'Add guests' : 'Choose your dates';
			this.#displayNextBtn();
		});
	}

	#handleNextBtn() {
		this.#btnNextElement.addEventListener('click', (event) => {
			if (this.#currentSelectionID !== 'selection-2') {
				this.#scrollToNextSelection();
				if (!this.#validation[this.#currentSelectionID]) {
					this.#hideNextBtn();
				}
				this.#btnBackElement.style.display = 'inline-block';
				if (this.#currentSelectionID === 'selection-1') {
					this.#btnNextElement.innerHTML = 'Choose your dates';
				} else {
					if (this.#validation[this.#currentSelectionID]) {
						this.#hideNextBtn();
						this.#displaySubmitBtn();
					}
				}
			}
		});
	}

	#handleCloseBtn() {
		let closeBtn = this.#holderElement.querySelector('.btn-close');

		const close = (event) => {
			event.preventDefault();
			document.querySelector('html').style.overflow = 'unset';
			this.#holderElement.style.height = '0';
			closeBtn.style.display = 'none';
			this.#holderElement.querySelector('.next-button-wrapper').style.position = 'absolute';
			this.#btnNextElement.style.position = 'initial';
			this.#btnSubmitElement.style.position = 'initial';
			this.#btnNextElement.style.opacity = '0';
			this.#btnSubmitElement.style.opacity = '0';
			this.#btnBackElement.style.display = 'none';
			this.#hideOptionSvg();
		}

		document.addEventListener('keyup', (event) => {
			if (event.key !== 'Escape') {
				return;
			}
			if (this.#holderElement.style.height !== '100vh') {
				return;
			}
			close(event);
		});

		closeBtn.addEventListener('click', close.bind(this));
	}

	#hideOptionSvg() {
		this.#holderElement.querySelectorAll('.booking-search-form-option').forEach(option => {
			option.querySelector('svg').style.opacity = '0';
		});
	}

	#scrollToPreviousSelection() {
		let currentSelection = this.#holderElement.querySelector('#' + this.#currentSelectionID);
		let previousSelection = currentSelection.previousElementSibling;
		currentSelection.classList.remove('active');
		previousSelection.classList.add('active');
		this.#getCurrentSelectionID();

		setTimeout(() => {
			previousSelection.scrollIntoView({ behavior: 'smooth' });
		}, 100);
	}

	#scrollToNextSelection() {
		let currentSelection = this.#holderElement.querySelector('#' + this.#currentSelectionID);
		let nextSelection = currentSelection.nextElementSibling;

		currentSelection.classList.remove('active');
		nextSelection.classList.add('active');
		this.#getCurrentSelectionID();

		setTimeout(() => {
			nextSelection.scrollIntoView({behavior: 'smooth'});
		}, 100);
	}

	#handleFormOptions() {
		this.#holderElement.querySelectorAll('.booking-search-form-option').forEach((option) => {
			option.addEventListener('click', (event) => {
				let input = option.querySelector('input');
				if (input) input.click();
				this.#displayNextBtn();
				this.#resetOptions();
				this.#handleSelectedOption(option);
			});
		});

		this.#handleOptionsIntro();
		this.#handleGuestNumSelection();
		this.#handleDatePicker();
	}

	#displayNextBtn() {
		this.#btnNextElement.style.display = 'inline-block';
	}

	#hideNextBtn() {
		this.#btnNextElement.style.display = 'none';
	}

	#displaySubmitBtn() {
		this.#btnSubmitElement.style.display = 'inline-block';
	}

	#hideSubmitBtn() {
		this.#btnSubmitElement.style.display = 'none';
	}

	#resetOptions() {
		this.#holderElement.querySelector('#' + this.#currentSelectionID)?.querySelectorAll('.option-icon').forEach((iconElement) => {
			iconElement.style.display = iconElement.classList.contains('clicked') ? 'none' : 'inline-block';
			iconElement.querySelector('svg').style.opacity = iconElement.classList.contains('clicked') ? '0' : '1';
		});
	}

	/**
	 * @param {Element} optionElement
	 */
	#handleSelectedOption(optionElement) {
		optionElement.querySelectorAll('.option-icon').forEach((iconElement) => {
			if (this.#currentSelectionID === 'selection-0' && iconElement.classList.contains('clicked')) {
				if (this.#bookingType) {
					if (this.#bookingType !== iconElement.dataset.value) {
						this.#bookingType = iconElement.dataset.value;
						this.#validation['selection-2'] = false;
						this.#resetDatePicker();
					}
				} else {
					this.#bookingType = iconElement.dataset.value;
				}

				if (this.#bookingType !== 'stay') {
					this.#hideNextBtn();
					this.#handleSubmitBtn();
				} else {
					this.#displayNextBtn();
					this.#hideSubmitBtn();
				}
			}
			iconElement.style.display = iconElement.classList.contains('clicked') ? 'inline-block' : 'none';
			iconElement.querySelector('svg').style.opacity = iconElement.classList.contains('clicked') ? '1' : '0';
			this.#validation[this.#currentSelectionID] = true;
		});
	}

	#handleOptionsIntro() {
		this.#holderElement.querySelectorAll('.mobile-option').forEach((option) => {
			option.addEventListener('click', (event) => {
				let activeIntro = this.#holderElement.querySelector('.intro-row').querySelector('#' + option.getAttribute('href').split('#')[1]);
				option.classList.remove('collapsed');
				this.#holderElement.querySelectorAll('.mobile-option').forEach((optionElement) => {
					if (optionElement !== option) {
						optionElement.classList.add('collapsed');
					}
				});
				this.#holderElement.querySelector('.intro-row').querySelectorAll('.intro').forEach((intro) => {
					if (intro !== activeIntro) {
						intro.classList.remove('show');
						activeIntro.classList.toggle('show');
					}
				});
			});
		});
	}

	#handleGuestNumSelection() {
		this.#guestsNum = this.#holderElement.querySelector('.guest-number');
		let guestNumInt = parseInt(this.#guestsNum.innerHTML);
		this.#holderElement.querySelector('.minus-guest').addEventListener('click', (event) => {
			guestNumInt > 0 ? guestNumInt -= 1 : 0;
			this.#setGuestNum(this.#guestsNum, guestNumInt);
		});
		this.#holderElement.querySelector('.plus-guest').addEventListener('click', (event) => {
			guestNumInt < this.#maxGuestNum ? guestNumInt += 1 : 0;
			this.#setGuestNum(this.#guestsNum, guestNumInt);
		});
	}

	/**
	 * @param {HTMLElement} guestNum
	 * @param {Number} guestNumInt
	 */
	#setGuestNum(guestNum, guestNumInt) {
		guestNum.innerHTML = guestNumInt.toString();
		if (guestNumInt > 0) {
			guestNum.style.backgroundColor = "#000";
			guestNum.style.color = "#FFF";
			this.#validation[this.#currentSelectionID] = true;
			this.#displayNextBtn();
		} else {
			guestNum.style.backgroundColor = "#FFF";
			guestNum.style.color = "#000";
			this.#validation[this.#currentSelectionID] = false;
			this.#hideNextBtn();
		}
	}

	#handleDatePicker() {
		let today = new Date();
		this.#currentMonth = today.getMonth();
		this.#currentYear = today.getFullYear();
		this.#picker = new Pikaday({
			minDate: today,
			onSelect: this.#handleDateSelect.bind(this),
			showDaysInNextAndPreviousMonths: true,
			i18n: {
				previousMonth : '',
				nextMonth     : '',
				months        : ['January','February','March','April','May','June','July','August','September','October','November','December'],
				weekdays      : ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'],
				weekdaysShort : ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
			}
		});

		this.#calendarElement.parentNode.insertBefore(this.#picker.el, this.#calendarElement.nextSibling);
	}

	/**
	 * @param {Date} date
	 */
	#handleDateSelect(date) {
		if (this.#bookingType === 'stay') {
			if (!this.#checkIn) {
				this.#checkIn = date;
			} else if (!this.#checkOut) {
				this.#checkOut = date;
				if (this.#checkIn > this.#checkOut) {
					this.#checkOut = this.#checkIn;
					this.#checkIn = date;
				}
			} else {
				this.#checkIn = date;
				this.#checkOut = null;
			}
			this.#switchDateSelectLabel(false);
			this.#picker.setStartRange(this.#checkIn);
			this.#picker.setEndRange(this.#checkOut);
			this.#picker.draw();

			if (this.#checkIn && this.#checkOut) {
				this.#validation[this.#currentSelectionID] = true;
				this.#handleSubmitBtn();
				this.#displaySubmitBtn();
			}
		} else {
			this.#checkIn = date;
			this.#checkOut = null;
			this.#picker.setStartRange(null);
			this.#picker.setEndRange(null);
			this.#picker.setDate(this.#checkIn, true);
			this.#validation[this.#currentSelectionID] = true;
			this.#handleSubmitBtn();
			this.#displaySubmitBtn();
		}
	}

	/**
	 * @param {boolean} isCheckIn
	 */
	#switchDateSelectLabel(isCheckIn) {
		this.#holderElement.querySelector('.start-date-label').style.display = isCheckIn ? 'block' : 'none';
		this.#holderElement.querySelector('.end-date-label').style.display = isCheckIn ? 'none' : 'block';
	}

	#resetDatePicker() {
		let today = new Date();
		this.#currentMonth = today.getMonth();
		this.#currentYear = today.getFullYear();
		this.#switchDateSelectLabel(true);
		this.#checkIn = null;
		this.#checkOut = null;
		this.#picker.setStartRange(null);
		this.#picker.setEndRange(null);
		this.#picker.setDate(null);
		this.#picker.clear();
	}

	#handleSubmitBtn() {
		let bookingLink;
		if (this.#bookingType === 'stay') {
			bookingLink = this.#properties.find(p => p.id === parseInt(this.#btnSubmitElement.dataset.propertyId))?.bookingLink;
			this.#bookingFormElement.setAttribute('action', bookingLink);
			this.#clearHiddenFields();

			const params = new URLSearchParams(bookingLink.replace(' ','').split('?')[1]);
			params.forEach(this.#createHiddenInput.bind(this));
			this.#createHiddenInput(DateFormat.format(this.#checkIn, DateFormat.ISO_8601), 'checkInDate');
			this.#createHiddenInput(DateFormat.format(this.#checkOut, DateFormat.ISO_8601), 'checkOutDate');
			this.#createHiddenInput(this.#guestsNum.innerHTML.toString(), 'items[0][adults]');
		} else if (this.#bookingType === 'dine') {
			CmsApi.fetchRestaurants().then(restaurants=>{
				bookingLink = restaurants.find(r => r.id === parseInt(this.#btnSubmitElement.dataset.restaurantId))?.bookLink?.link;
				this.#bookingFormElement.setAttribute('action', bookingLink);

				this.#clearHiddenFields();
				const params = new URLSearchParams(bookingLink.replace(' ','').split('?')[1]);
				params.forEach(this.#createHiddenInput.bind(this));

				this.#displaySubmitBtn();
			});
		} else {
			bookingLink = this.#btnSubmitElement.dataset.functionLink;
			this.#bookingFormElement.setAttribute('action', bookingLink);
			this.#clearHiddenFields();
			this.#displaySubmitBtn();
		}
	}

	#clearHiddenFields() {
		this.#bookingFormElement.innerHTML = '';
	}

	/**
	 * @param {String} value
	 * @param {String} key
	 */
	#createHiddenInput(value, key) {
		this.#bookingFormElement.innerHTML += `<input type="hidden" name="${key}" value="${value}">`;
	}
}
